import { useId } from 'react';
import { cn } from '@/lib/utils';

interface CheckboxProps {
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
  label?: string;
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
  /**
   * `aria-labelledby` — the id of an element whose text names this checkbox.
   *
   * For when the label is RICH (mixed weights, inline counts) and so cannot go
   * through the plain-string `label` prop, but must still be the control's
   * accessible name. The plain-string label uses a native `htmlFor`
   * association; rich external labels point at their visible text from here.
   *
   * Takes precedence over `ariaLabel` when both are set: a name pointing at
   * visible on-screen text is more truthful than a parallel string that can
   * drift from what the user is actually reading.
   */
  ariaLabelledBy?: string;
}

export default function Checkbox({
  checked,
  indeterminate,
  onChange,
  label,
  className,
  disabled,
  ariaLabel,
  ariaLabelledBy,
}: CheckboxProps) {
  const checkboxId = useId();
  const displayChecked = checked && !indeterminate;
  const displayIndeterminate = indeterminate;

  return (
    <div className={cn('flex items-center', className)}>
      <button
        id={checkboxId}
        type="button"
        role="checkbox"
        aria-checked={indeterminate ? 'mixed' : checked}
        // Only ONE of the two is emitted: `aria-labelledby` wins where both are
        // given, and `aria-label` is suppressed rather than left to compete —
        // an element carrying both gives assistive tech two different names for
        // the same control.
        aria-label={ariaLabelledBy ? undefined : ariaLabel}
        aria-labelledby={ariaLabelledBy}
        disabled={disabled}
        onClick={() => onChange?.(indeterminate ? true : !checked)}
        // `focus-visible:`, NOT `focus:` — Radix autofocuses the first focusable
        // element when a dialog opens, so a plain `focus:` ring painted the
        // first checkbox the moment the dialog appeared and read as a stuck
        // hover/selected state. Keyboard focus still rings. Matches the
        // prevailing treatment in radio-group.tsx and input.tsx.
        className="relative h-5 w-5 rounded-sm border border-edge bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {displayChecked && (
          <div className="absolute inset-0 rounded-sm bg-sage border border-sage">
            <svg className="absolute inset-0 h-full w-full p-[1px]" fill="none" viewBox="0 0 16 16">
              <path
                d="M2 8L6 13L14 3"
                stroke="#FCFBF8"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
        {displayIndeterminate && (
          <div className="absolute inset-0 rounded-sm bg-sage border border-sage">
            <svg className="absolute inset-0 h-full w-full" fill="none" viewBox="0 0 16 16">
              <path d="M3 8H13" stroke="#FCFBF8" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
        )}
      </button>
      {label && (
        <label
          htmlFor={checkboxId}
          className="ml-3 text-sm font-medium text-ink-secondary cursor-pointer select-none"
        >
          {label}
        </label>
      )}
    </div>
  );
}
